import { useEffect, useMemo, useState } from 'react';

import { capitalize } from 'lodash';
import { XIcon } from 'lucide-react';
import { Input, DefaultPopover, Separator, Select, ToggleOptionGroup, ToggleOptionGroupItem, ColorPickerInputGroup, InputGroup, List } from 'twyne-new.ui';

import { parseSpacing, useCraftProp } from '../utils';
import { BORDER_RADIUS_SIZE, UNIT_OPTIONS } from '../utils/predefined_sizes';

import PaletteColors from './Color/Palette';
import SelectUnit from './SelectUnit';
import SpacingProperty from './Spacing';
import { ListContent, ListLabel, ListWrapper, ListItem } from './components/list';

export default function BorderProperty({ prefix, title = capitalize(prefix), itens, widthOptions, generalTab: GeneralTab = TabGeneral, inputParser = parseSpacing, ...props }) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('general')

  const { value: width } = useCraftProp(`${prefix}.width`)
  const { value: style } = useCraftProp(`${prefix}.style`)
  const { value: color } = useCraftProp(`${prefix}.color`)
  const { value } = useCraftProp(`${prefix}.value`)
  const { value: blur } = useCraftProp(`${prefix}.blur`)
  const { value: x } = useCraftProp(`${prefix}.x`)
  const { value: y } = useCraftProp(`${prefix}.y`)
  const { value: unit } = useCraftProp(`${prefix}.unit`)
  const { value: radius } = useCraftProp(`${prefix}.radius`)

  const styleLabel = useMemo(() => STYLE_OPTIONS.find(option => option.value === style)?.label, [style])

  const inputValue = useMemo(() => {
    const widthParsed = inputParser(width, { width, style, color, value, blur, x, y, unit, radius })
    const radiusParsed = parseSpacing(radius)?.replaceAll(',', '')
    const colorParsed = color && (typeof color === 'string' ? color : color.hex) || ''
    const classes = [widthParsed, colorParsed, styleLabel, radiusParsed ? `radius:${radiusParsed}` : ''].join(' ')
    const removePrefix = classes.replace(new RegExp(`${prefix}-`, 'g'), '').replace(new RegExp(`rounded-`, 'g'), '')
    return removePrefix.trim().split(' ').join(', ').replaceAll(', ,', ',').replaceAll('[', '').replaceAll(']', '')
  }, [color, styleLabel, prefix, inputParser, width, style, value, blur, x, y, unit, radius])

  const handleClickColorTrigger = () => {
    setOpen(true)
    setTab('color')
  }

  const handleClickInputTrigger = () => {
    setOpen(true)
    setTab('general')
  }

  return <>
    <div className="flex items-center">
      <ColorPickerInputGroup
        value={color}
        components={['preview']}
        onClick={handleClickColorTrigger}
      />

      <Input value={inputValue || ''} readOnly variant='accent' onClick={handleClickInputTrigger} />
      <DefaultPopover
        wrapperProps={{ open, onOpenChange: setOpen }}
        trigger={<div></div>}
        triggerProps={{ asChild: true }}
        contentProps={{ className: 'p-0', side: 'bottom', align: 'center' }}
      >
        <div className='p-2 pb-0'>
          <div className='flex justify-between items-center'>
            {title}
            <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <Separator className='m-0 my-2' />

          <ToggleOptionGroup className='mb-4' type='single' value={tab} onValueChange={setTab}>
            <ToggleOptionGroupItem value='general'>General</ToggleOptionGroupItem>
            <ToggleOptionGroupItem value='color'>Color</ToggleOptionGroupItem>
          </ToggleOptionGroup>

        </div>

        {tab === 'color' ? <List><PaletteColors className='pr-2' name={`${prefix}.color`} /></List> : <GeneralTab prefix={prefix} widthOptions={widthOptions} itens={itens} {...props} />}

      </DefaultPopover>
    </div>
  </>
}

const TabGeneral = ({ prefix, widthOptions, itens }) => <>
  <ListWrapper>
    <div className='flex flex-col gap-2'>
      <ListItem>
        <ListLabel>Width</ListLabel>
        <ListContent>
          <Width prefix={prefix} widthOptions={widthOptions} />
        </ListContent>
      </ListItem>

      {itens.includes('style') && <>
        <ListItem>
          <ListLabel>Style</ListLabel>
          <ListContent>
            <Style prefix={prefix} />
          </ListContent>
        </ListItem>
      </>}

      {itens.includes('offset') && <>
        <ListItem>
          <ListLabel>Offset</ListLabel>
          <ListContent>
            <Offset prefix={prefix} />
          </ListContent>
        </ListItem>
      </>}

      {itens.includes('radius') && <>
        <ListItem>
          <ListLabel>Radius</ListLabel>
          <ListContent className='col-span-3'>
            <Radius />
          </ListContent>
        </ListItem>
      </>}
    </div>
  </ListWrapper>
</>

const Width = ({ prefix, widthOptions }) => {
  const options = [{ label: "all", value: [{ value: prefix, type: prefix }] }]

  if (widthOptions.includes('axis')) {
    options.push({ label: "axis", value: [{ value: "x", type: `${prefix}-x` }, { value: "y", type: `${prefix}-y` }] })
  }

  if (widthOptions.includes('individual')) {
    options.push({ label: "individual", value: [{ value: "t", type: `${prefix}-t` }, { value: "r", type: `${prefix}-r` }, { value: "b", type: `${prefix}-b` }, { value: "l", type: `${prefix}-l` }] })
  }

  return <div>
    <SpacingProperty name={`${prefix}.width`} options={options} />
  </div>
}

const Style = ({ prefix }) => {
  const { value: style, setValue: setStyle } = useCraftProp(`${prefix}.style`)

  return <Select value={style} onChange={option => setStyle(option?.value)} options={STYLE_OPTIONS} variant='accent' />
}

const Offset = ({ prefix }) => {
  const options = [
    { label: "all", value: [{ value: `${prefix}-offset`, type: `${prefix}-offset` }] },
  ]

  return <div>
    <SpacingProperty name={`${prefix}.offset`} options={options} />
  </div>
}

const Radius = () => {
  const options = [
    { label: "all", value: [{ value: 'rounded', type: 'rounded' }] },
    { label: "individual", value: [{ value: "t", type: `rounded-t` }, { value: "r", type: `rounded-r` }, { value: "b", type: `rounded-b` }, { value: "l", type: `rounded-l` }] },
    { label: "corners", value: [{ value: "tl", type: `rounded-tl` }, { value: "tr", type: `rounded-tr` }, { value: "bl", type: `rounded-bl` }, { value: "br", type: `rounded-br` }] },
  ]

  return <div>
    <SpacingProperty name='border.radius' defaultUnit='px' options={options} inputValue={BorderRadiusInputValue} unitOptions={UNIT_OPTIONS} predefined />
  </div>
}

const BorderRadiusInputValue = ({ onChange, disabled, value, unit, onChangeUnit, multiple, ...props }) => {
  const [isArbitrary, setIsArbitrary] = useState()

  useEffect(() => {
    if (isArbitrary === undefined) {
      setIsArbitrary(!multiple && value && (value === 'arbitrary' || !BORDER_RADIUS_SIZE.some(opt => opt.value === value)))
    }
  }, [isArbitrary, value, multiple])

  const handleChangeArbitrary = (value = '') => {
    const arbitrary = value === 'arbitrary'
    setIsArbitrary(arbitrary)
    onChange(!arbitrary ? value : '')
  }

  return <>
    <div className='flex flex-col gap-1 w-full'>
      {!multiple ? <>
        <Select
          value={isArbitrary ? 'arbitrary' : value || ''}
          variant='accent'
          onChange={option => handleChangeArbitrary(option?.value)}
          options={[{ label: 'Arbitrary', value: 'arbitrary' }, ...BORDER_RADIUS_SIZE]}
          disabled={disabled}
          clearable
          className='w-full'
          {...props}
        />
      </> : <>
        <Input
          variant='accent'
          type='number'
          size='sm'
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          value={!disabled ? value : ''}
          onKeyDown={e => e.key === ',' && e.preventDefault()}
          {...props}
        />
      </>}

      {isArbitrary ? <>
        <InputGroup variant='accent'>
          <Input
            variant='accent'
            type='number'
            size='sm'
            onChange={e => onChange(e.target.value)}
            value={value}
            className='w-3/5'
          />

          <SelectUnit
            value={value}
            unit={unit}
            onChangeUnit={onChangeUnit}
            options={UNIT_OPTIONS}
            variant='accent'
          />
        </InputGroup>
      </> : null}
    </div>
  </>
}

const STYLE_OPTIONS = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
  { value: 'none', label: 'None' },
]
